import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { useContext, useState } from 'react';
import { AppContext } from 'context/app-context';
import { setModal } from 'store/features/modelSlice';
import { initModal } from 'helpers/utils';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import InputField from 'components/common/input-field';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import { createTireSize, updateTireSize } from 'store/features/vehicleConfigSlice';
import toast from 'react-hot-toast';
import { ITireSize } from 'interfaces/vehicle-config-interfaces';
import { useTranslation } from 'react-i18next';

const AddEditTireSize = () => {
  const dispatch = useAppDispatch();
  const {
    modal: {
      content: { title, formFor, callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { selectedTireSize } = useAppSelector(state => state.vehicleConfig);
  const { setLoader } = useContext(AppContext);
  const [tireSize, setTireSize] = useState<ITireSize>(
    formFor && selectedTireSize ? selectedTireSize : { name: '' },
  );
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');
  const handleAddTireSize = async () => {
    try {
      setLoader(true);
      await dispatch(createTireSize({ name: tireSize?.name }))
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('SUCCESS_TIRE_SIZE_ADDED_TOAST_MESSAGE'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleEditTireSize = async () => {
    try {
      setLoader(true);
      await dispatch(
        updateTireSize({
          tire_size_id: tireSize?._id || '',
          request: { tire_size_id: tireSize?._id || '', name: tireSize?.name },
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('SUCCESS_TIRE_SIZE_UPDATED_TOAST_MESSAGE'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSubmit = () => {
    try {
      if (!tireSize?.name?.trim()) {
        setError(t('ERROR_TIRE_SIZE_TOAST_MESSAGE'));
        return;
      }
      formFor === 'EDIT' ? handleEditTireSize() : handleAddTireSize();
    } catch (error) {
      setLoader(false);
    }
  };
  return (
    <div className="flex flex-wrap max-w-[450px] w-[450px] gap-y-4 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">{title}</label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh- 200px)] gap-y-1">
        <div className="w-full">
          <label className="font-medium text-xs text-black2 w-full">{t('TIRE_SIZE')}</label>
          <InputField
            onChange={data => setTireSize({ ...tireSize, name: data })}
            error={error}
            value={tireSize.name}
            placeholder={t('TIRE_SIZE_PLACEHOLDER')}
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-4">
        <PrimaryDarkButton
          label={formFor === 'EDIT' ? t('SAVE_CHANGES') : t('ADD_TIRE_SIZE')}
          onClick={() => handleSubmit()}
          width="w-1/3"
        />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/3"
          onClick={() => dispatch(setModal(initModal))}
        />
      </div>
    </div>
  );
};

export default AddEditTireSize;
